<template>
  <span class="brandIconContainer" :title="title" :class="{ showFocus, focused }" @click="emit('click')">
    <svg v-if="isFlatIcon || isPublicIcon" :viewBox="includeViewBox ? '0 0 24 24' : undefined"
         :v-key="name" class="icon" :style="propStyles">
      <use :href="`${baseImage}#${imageId}`"></use>
    </svg>
    <div v-else-if="isFullColour">
      <img :src="baseImage" alt="" />
      <svg v-if="showFullColourFocus" :v-key="name" class="icon" :style="propStyles" :class="{heavy, fade}">
        <use class="focusIndication" v-if="(showFullColourFocus || focused)"
             :href="`${focusImage}#${imageId}-focus`"></use>
      </svg>
    </div>
    <svg v-else :v-key="name" class="icon" :style="propStyles" :class="{heavy, fade}">
      <use class="focusIndication" v-if="focusImage && (showFocus || focused)"
           :href="`${focusImage}#${imageId}-focus`"></use>
      <use :href="`${baseImage}#${imageId}`"></use>
    </svg>
  </span>
</template>

<script setup lang="ts">
import { computed, ComputedRef, watch } from 'vue';

import pinia from '@/stores';
import { useStaticResourcesStore } from '@/stores/StaticResources';

const staticResourcesStore = useStaticResourcesStore(pinia);

interface Props {
  name: string;
  title?: string;
  showFocus?: boolean;
  showFullColourFocus?: boolean;
  focused?: boolean;
  /**
   * For situations that require icon usage at small sizes,
   * optionally set the following 'heavy' property to true to give the icon a bolder/thicker appearance
   */
  heavy?: boolean;
  fade?: boolean;
  fillColour?: string | null;
  includeViewBox?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showFocus: true,
  showFullColourFocus: false,
  focused: false,
  heavy: false,
  fade: false,
  fillColour: null,
  includeViewBox: true,
});

interface Emits {
  (e: 'click'): void,
}
const emit = defineEmits<Emits>();

const isPublicIcon: ComputedRef<boolean> = computed(() => {
  return props.name.startsWith('/public/');
});

const isFlatIcon: ComputedRef<boolean> = computed(() => {
  return props.name.startsWith('flat/');
});

const isFullColour: ComputedRef<boolean> = computed(() => {
  return props.name.includes('fullColour/');
});

const imageId: ComputedRef<string> = computed(() => {
  return props.name.replace(/.*\//, '');
});

const baseImage: ComputedRef<string> = computed(() => {
  return staticResourcesStore.resolvedPaths[`${props.name}.svg`];
});

const focusImageName: ComputedRef<string | undefined> = computed(() => {
  if (!props.name) {
    return;
  }
  if (isFlatIcon.value || isPublicIcon.value || (isFullColour.value && !props.showFullColourFocus)) {
    return;
  }
  if (!props.showFocus) {
    return;
  }
  return `${props.name}-focus.svg`;
});

const focusImage: ComputedRef<string | undefined> = computed(() => {
  if (!focusImageName.value) {
    return;
  }
  return staticResourcesStore.resolvedPaths[focusImageName.value];
});

const propStyles: ComputedRef<Record<string, string>> = computed(() => {
  let styleObject = {};
  if (props.fillColour) {
    styleObject = {
      fill: props.fillColour,
    };
  }
  return styleObject;
});

async function resolveImagePath(name: string): Promise<string> {
  let resolved: string = staticResourcesStore.resolvedPaths[name];
  if (!resolved) {
    if (isPublicIcon.value) {
      resolved = name.substring(7);
    } else {
      resolved = await require(`@/assets/images/svg/icon/${name}`);
    }
    staticResourcesStore.setResolvedPath(name, resolved);
  }
  return resolved;
}

watch(() => props.name, (newValue: string) => {
  if (!newValue) {
    return;
  }
  const toResolve: string = `${newValue}.svg`;
  if (isPublicIcon.value) {
    staticResourcesStore.setResolvedPath(toResolve, toResolve.substring(7));
  } else {
    resolveImagePath(toResolve);
  }
}, { immediate: true });

watch(() => focusImageName.value, (newValue?: string) => {
  if (!newValue) {
    return;
  }
  resolveImagePath(newValue);
}, { immediate: true });
</script>

<style scoped lang="scss">
  svg {
    &.icon {
      display: inline-block;
      width: 100%;
      height: 100%;
      stroke-width: 0;
      stroke-linejoin: round;
      stroke: currentColor;
      fill: currentColor;
      transition: all $transition-time-short ease-in-out;
      .disabled & {
        color: $colour-icon-primary-disabled;
      }
      &.heavy {
        stroke-width: 0.25px;
      }
      &.fade {
        color: $colour-border-mid;
      }
    }
  }

  .brandIconContainer {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    align-self: center;
    justify-self: center;
    width: 1.5rem;
    height: 1.5rem;
    div {
      position: relative;
      width: 100%;
      height: 100%;
    }
    .focusIndication {
      transition: all $transition-time-short ease-in-out;
      opacity: 0;
      fill: currentColor;
      stroke: currentColor;
    }
  }

  .focused {
    &.brandIconContainer {
      .focusIndication {
        opacity: 0.7;
      }
    }
  }

  img {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  // Ordering of following rules important to prevent grouping selectors during SCSS transpilation
  :focus-visible .showFocus {
    &.brandIconContainer {
      .focusIndication {
        opacity: 0.7;
      }
    }
  }

  // Following two rules are for browsers without focus-visible support
  :focus:not(:focus-visible) .showFocus {
    &.brandIconContainer {
      .focusIndication {
        opacity: 0;
      }
    }
  }

  :focus .showFocus {
    &.brandIconContainer {
      .focusIndication {
        opacity: 0.7;
      }
    }
  }
</style>
